<template>
  <div class="extra-fields-list">
    <a-card title="历史数据额外字段" size="small">
      <a-spin :spinning="loading">
        <div v-if="!hasExtraContent" class="empty-state">
          <a-empty description="暂无额外字段数据" />
        </div>
        <div v-else class="fields-container">
          <div
            v-for="field in extraFields"
            :key="field.placeholder"
            class="field-item"
            :class="{ 'field-item-clickable': hasValue(field) }"
            :style="{ backgroundColor: field.backgroundColor }"
            @click="handleFieldClick(field)"
          >
            <div class="field-header">
              <span class="field-name">{{ field.display_name }}</span>
              <a-tag v-if="hasValue(field)" color="green" size="small">有值</a-tag>
              <a-tag v-else color="default" size="small">无值</a-tag>
            </div>
            <div class="field-placeholder">
              <code>{{ field.placeholder }}</code>
            </div>
            <div v-if="hasValue(field)" class="field-value">
              <div class="value-preview" :class="{ 'value-expandable': isExpandable(field) }">
                <div v-if="(field.data_field === 'fault_code_list' || field.data_field === 'engineFaultCodes' || field.data_field === 'dcuFaultCodes' || field.data_field_camel === 'engineFaultCodes' || field.data_field_camel === 'dcuFaultCodes') && Array.isArray(field.value)" class="array-list">
                  <div v-for="(item, idx) in field.value.slice(0, 5)" :key="idx" class="array-item">
                    {{ formatArrayItem(item, field.data_field_camel || field.data_field) }}
                  </div>
                  <div v-if="field.value.length > 5" class="more-text">
                    ... 还有 {{ field.value.length - 5 }} 项
                  </div>
                </div>
                <div v-else-if="(field.data_field === 'selected_streams' || field.data_field === 'selectedStreams' || field.data_field === 'supportedDataStreams' || field.data_field === 'collectedDataStreams' || field.data_field_camel === 'selectedStreams' || field.data_field_camel === 'supportedDataStreams' || field.data_field_camel === 'collectedDataStreams') && Array.isArray(field.value)" class="array-list">
                  <div v-for="(item, idx) in field.value.slice(0, 5)" :key="idx" class="array-item">
                    {{ formatArrayItem(item, field.data_field) }}
                  </div>
                  <div v-if="field.value.length > 5" class="more-text">
                    ... 还有 {{ field.value.length - 5 }} 项
                  </div>
                </div>
                <div v-else class="text-preview">
                  {{ formatValue(field) }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="availableWorkerRefs.length > 0" class="worker-ref-section">
            <div class="section-title">当前 Worker 可引用的历史 Worker 标签及替换数据</div>
            <div class="worker-ref-list">
              <div
                v-for="workerRef in availableWorkerRefs"
                :key="workerRef.placeholder"
                class="worker-ref-item"
              >
                <div class="worker-ref-header">
                  <span class="worker-ref-name">{{ workerRef.display_name }}</span>
                  <a-tag :color="getWorkerRefStatusColor(workerRef.status)" size="small">{{ getWorkerRefStatusText(workerRef.status) }}</a-tag>
                </div>
                <div class="worker-ref-placeholder">
                  <code>{{ workerRef.placeholder }}</code>
                </div>
                <div class="worker-ref-value">
                  <pre>{{ formatWorkerRefValue(workerRef.value) }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a-spin>
    </a-card>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { message } from 'ant-design-vue';
import { PLACEHOLDER_COLORS } from '@/constants/knowledgeColors';

export default {
  name: 'ExtraFieldsList',
  props: {
    planData: {
      type: Object,
      default: null
    },
    availableWorkerRefs: {
      type: Array,
      default: () => []
    }
  },
  emits: ['field-click'],
  setup(props, { emit }) {
    const loading = ref(false);

    // 计算额外字段列表（只显示需要跳转的复杂字段，按自定义顺序排序）
    const extraFields = computed(() => {
      if (!props.planData) {
        return [];
      }

      // 使用驼峰命名的占位符，按照用户提供的变量定义
      // 只显示复杂字段，简单元数据字段（task_id、control_unit、fault_phenomenon、identified_system）不显示
      const fieldOrder = [
        { placeholder: '{engineVersion}', display_name: '发动机版本', data_field_camel: 'engineVersion', data_field_snake: 'ecu_version_info_list', placeholder_key: 'engineVersion' },
        { placeholder: '{dcuVersion}', display_name: 'DCU版本', data_field_camel: 'dcuVersion', data_field_snake: 'dcu_version', placeholder_key: 'dcuVersion' },
        { placeholder: '{engineFaultCodes}', display_name: '发动机故障码', data_field_camel: 'engineFaultCodes', data_field_snake: 'fault_code_list', placeholder_key: 'engineFaultCodes' },
        { placeholder: '{dcuFaultCodes}', display_name: 'DCU故障码', data_field_camel: 'dcuFaultCodes', data_field_snake: 'dcu_fault_codes', placeholder_key: 'dcuFaultCodes' },
        { placeholder: '{supportedDataStreams}', display_name: '支持的数据流', data_field_camel: 'supportedDataStreams', data_field_snake: 'stream_list', placeholder_key: 'supportedDataStreams' },
        { placeholder: '{collectedDataStreams}', display_name: '采集的数据流', data_field_camel: 'collectedDataStreams', data_field_snake: 'csv_data_stream', placeholder_key: 'collectedDataStreams' },
        { placeholder: '{userFaultPhenomenon}', display_name: '故障现象', data_field_camel: 'userFaultPhenomenon', data_field_snake: 'fault_phenomenon', placeholder_key: 'userFaultPhenomenon' },
        { placeholder: '{userAdditionalInfo}', display_name: '用户附加信息', data_field_camel: 'userAdditionalInfo', data_field_snake: 'user_additional_info', placeholder_key: 'userAdditionalInfo' },
        { placeholder: '{workingConditions}', display_name: '工况定义', data_field_camel: 'workingConditions', data_field_snake: 'working_conditions', placeholder_key: 'workingConditions' },
        { placeholder: '{selectedStreams}', display_name: '选中的数据流', data_field_camel: 'selectedStreams', data_field_snake: 'selected_streams', placeholder_key: 'selectedStreams' }
      ];

      return fieldOrder
        .map((field, index) => {
          // 优先使用驼峰命名读取数据，如果没有则使用下划线命名
          let value = props.planData[field.data_field_camel] || props.planData[field.data_field_snake];
          
          // 解析JSON字符串（对于数组类型的字段）
          const jsonArrayFields = ['engineFaultCodes', 'dcuFaultCodes', 'supportedDataStreams', 'collectedDataStreams', 'selectedStreams', 'fault_code_list', 'selected_streams', 'stream_list', 'csv_data_stream'];
          if (typeof value === 'string' && jsonArrayFields.includes(field.data_field_camel) || jsonArrayFields.includes(field.data_field_snake)) {
            try {
              value = JSON.parse(value);
            } catch (e) {
              // 保持原值
            }
          }

          // 获取对应的背景色（优先使用驼峰命名，如果没有则使用下划线命名）
          const backgroundColor = PLACEHOLDER_COLORS[field.placeholder_key] || 
                                 PLACEHOLDER_COLORS[field.data_field_snake] || 
                                 '#f5f5f5';

          // 确定实际使用的字段名（用于后续格式化）
          const actualDataField = value === props.planData[field.data_field_camel] ? field.data_field_camel : field.data_field_snake;
          
          return {
            placeholder: field.placeholder,
            display_name: field.display_name,
            data_field: actualDataField, // 使用实际读取到的字段名
            data_field_camel: field.data_field_camel, // 保留驼峰命名，用于格式化判断
            data_field_snake: field.data_field_snake, // 保留下划线命名，用于格式化判断
            placeholder_key: field.placeholder_key,
            value: value,
            backgroundColor: backgroundColor,
            sort_order: index
          };
        })
        .filter(field => field.value !== null && field.value !== undefined && field.value !== '');
    });

    const hasExtraContent = computed(() => (
      extraFields.value.length > 0 || props.availableWorkerRefs.length > 0
    ));

    // 判断字段是否有值
    const hasValue = (field) => {
      const value = field.value;
      if (value === null || value === undefined || value === '') {
        return false;
      }
      
      // 处理数组
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      
      // 处理对象
      if (typeof value === 'object') {
        return Object.keys(value).length > 0;
      }
      
      // 处理字符串
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      
      return true;
    };

    // 格式化值（用于预览）
    const formatValue = (field) => {
      const value = field.value;
      if (!value) return '';

      // 处理数组类型
      if (Array.isArray(value)) {
        // 故障码列表 - 显示具体值
        if (field.data_field === 'fault_code_list' || field.data_field === 'engineFaultCodes' || field.data_field === 'dcuFaultCodes' ||
            field.data_field_camel === 'engineFaultCodes' || field.data_field_camel === 'dcuFaultCodes') {
          if (value.length === 0) return '无故障码';
          
          // 提取故障码显示（假设每个对象有 code 或 faultCode 字段）
          const codes = value.map(item => {
            if (typeof item === 'string') return item;
            if (item.code) return item.code;
            if (item.faultCode) return item.faultCode;
            if (item.dtc) return item.dtc;
            // 如果是对象，尝试提取关键信息
            if (typeof item === 'object') {
              const keys = Object.keys(item);
              if (keys.length > 0) {
                // 返回第一个看起来像代码的字段
                const codeKey = keys.find(k => k.toLowerCase().includes('code') || k.toLowerCase().includes('dtc'));
                if (codeKey) return item[codeKey];
                // 否则返回第一个值
                return item[keys[0]];
              }
            }
            return JSON.stringify(item);
          }).filter(Boolean);
          
          if (codes.length > 3) {
            return codes.slice(0, 3).join(', ') + ` 等${codes.length}个故障码`;
          }
          return codes.join(', ');
        }
        
        // 数据流列表 - 显示具体值（包括选中的数据流、支持的数据流、采集的数据流）
        if (field.data_field === 'selected_streams' || field.data_field === 'selectedStreams' ||
            field.data_field === 'supportedDataStreams' || field.data_field === 'collectedDataStreams' ||
            field.data_field === 'supported_data_streams' || field.data_field === 'stream_list' ||
            field.data_field_camel === 'selectedStreams' || field.data_field_camel === 'supportedDataStreams' || 
            field.data_field_camel === 'collectedDataStreams') {
          if (value.length === 0) return '无数据流';
          
          // 提取数据流名称
          const streams = value.map(item => {
            if (typeof item === 'string') return item;
            if (item.name) return item.name;
            if (item.streamName) return item.streamName;
            if (item.label) return item.label;
            if (typeof item === 'object') {
              const keys = Object.keys(item);
              if (keys.length > 0) return item[keys[0]];
            }
            return JSON.stringify(item);
          }).filter(Boolean);
          
          if (streams.length > 5) {
            return streams.slice(0, 5).join(', ') + ` 等${streams.length}个数据流`;
          }
          return streams.join(', ');
        }
        
        // 其他数组
        return `数组 (${value.length} 项)`;
      }

      // 处理字符串
      if (typeof value === 'string') {
        // CSV内容 - 只显示前几行
        if (field.data_field === 'csv_data_stream' || field.data_field === 'collectedDataStreams' ||
            field.data_field_camel === 'collectedDataStreams') {
          const lines = value.split('\n').filter(line => line.trim());
          if (lines.length > 3) {
            return lines.slice(0, 3).join('\n') + `\n... (共${lines.length}行)`;
          }
          return value;
        }
        
        // 其他文本 - 截断显示
        if (value.length > 100) {
          return value.substring(0, 100) + '...';
        }
        return value;
      }

      // 处理对象
      if (typeof value === 'object') {
        return `对象 (${Object.keys(value).length} 个字段)`;
      }

      return String(value);
    };

    // 判断字段是否可展开
    const isExpandable = (field) => {
      return Array.isArray(field.value) || (typeof field.value === 'string' && field.value.length > 100);
    };

    // 格式化数组项
    const formatArrayItem = (item, fieldName) => {
      if (typeof item === 'string') return item;
      
      if (fieldName === 'fault_code_list' || fieldName === 'engineFaultCodes' || fieldName === 'dcuFaultCodes' ||
          fieldName === 'engine_fault_codes' || fieldName === 'dcu_fault_codes') {
        // 故障码列表
        if (item.code) return `${item.code}${item.description ? ' - ' + item.description : ''}`;
        if (item.faultCode) return `${item.faultCode}${item.desc ? ' - ' + item.desc : ''}`;
        if (item.dtc) return `${item.dtc}${item.description ? ' - ' + item.description : ''}`;
      }
      
      if (fieldName === 'selected_streams' || fieldName === 'selectedStreams' || fieldName === 'supportedDataStreams' || fieldName === 'collectedDataStreams' ||
          fieldName === 'supported_data_streams' || fieldName === 'collected_data_streams' || fieldName === 'stream_list' || fieldName === 'csv_data_stream') {
        // 选中的数据流列表
        if (item.name) return item.name;
        if (item.streamName) return item.streamName;
        if (item.label) return item.label;
      }
      
      // 默认：返回对象的第一个值或 JSON 字符串
      if (typeof item === 'object') {
        const keys = Object.keys(item);
        if (keys.length > 0) return String(item[keys[0]]);
      }
      
      return JSON.stringify(item);
    };

    const formatWorkerRefValue = (value) => {
      if (value === null || value === undefined || value === '') {
        return '暂无替换数据';
      }

      if (typeof value === 'string') {
        return value;
      }

      try {
        return JSON.stringify(value, null, 2);
      } catch (error) {
        return String(value);
      }
    };

    const getWorkerRefStatusText = (status) => {
      switch (status) {
        case 'success':
        case 'completed':
          return '有结果';
        case 'failed':
          return '执行失败';
        case 'skipped':
          return '已跳过';
        case 'pending':
          return '待执行';
        case 'executing':
          return '执行中';
        default:
          return '无日志';
      }
    };

    const getWorkerRefStatusColor = (status) => {
      switch (status) {
        case 'success':
        case 'completed':
          return 'green';
        case 'failed':
          return 'red';
        case 'skipped':
          return 'orange';
        case 'pending':
          return 'default';
        case 'executing':
          return 'blue';
        default:
          return 'default';
      }
    };

    // 字段点击处理
    const handleFieldClick = (field) => {
      if (!hasValue(field)) {
        message.info('该字段无值');
        return;
      }

      // 提取占位符名称（去掉大括号）
      let placeholderName = field.placeholder.replace(/[{}]/g, '');
      
      // 占位符名称映射（用于处理驼峰命名和下划线命名的映射关系）
      // 提示词中可能使用驼峰命名或下划线命名，为了确保跳转成功，同时发送多个可能的占位符名称
      const placeholderMapping = {
        // 驼峰命名映射
        'engineVersion': ['engineVersion', 'engine_version', 'engine_info'],
        'dcuVersion': ['dcuVersion', 'dcu_version', 'aftertreatmentVersion', 'aftertreatment_version'],
        'engineFaultCodes': ['engineFaultCodes', 'engine_fault_codes', 'fault_codes', 'fault_code_list'],
        'dcuFaultCodes': ['dcuFaultCodes', 'dcu_fault_codes', 'aftertreatmentFaultCodes', 'aftertreatment_fault_codes'],
        'supportedDataStreams': ['supportedDataStreams', 'supported_data_streams', 'stream_list'],
        'collectedDataStreams': ['collectedDataStreams', 'collected_data_streams', 'csv_data_stream', 'dataflow', 'csv_content'],
        'userFaultPhenomenon': ['userFaultPhenomenon', 'user_fault_phenomenon', 'fault_phenomenon'],
        'userAdditionalInfo': ['userAdditionalInfo', 'user_additional_info'],
        'workingConditions': ['workingConditions', 'working_conditions'],
        'selectedStreams': ['selectedStreams', 'selected_streams'],
        // 下划线命名映射（兼容旧格式）
        'csv_content': ['collectedDataStreams', 'csv_content', 'csv_data_stream', 'collected_data_streams'],
        'csv_data_stream': ['collectedDataStreams', 'csv_data_stream', 'csv_content', 'collected_data_streams'],
        'fault_code_list': ['engineFaultCodes', 'fault_code_list', 'engine_fault_codes', 'fault_codes'],
        'working_conditions': ['workingConditions', 'working_conditions'],
        'selected_streams': ['selectedStreams', 'selected_streams']
      };
      
      // 如果当前占位符在映射中，使用映射后的名称列表
      const possiblePlaceholders = placeholderMapping[placeholderName] || [placeholderName];
      
      // 触发事件，通知父组件跳转到对应位置
      emit('field-click', {
        placeholder: placeholderName, // 主要占位符名称
        possiblePlaceholders: possiblePlaceholders, // 所有可能的占位符名称（用于跳转匹配）
        fieldName: field.display_name,
        value: field.value
      });
    };

    return {
      loading,
      extraFields,
      hasExtraContent,
      hasValue,
      formatValue,
      isExpandable,
      formatArrayItem,
      handleFieldClick,
      formatWorkerRefValue,
      getWorkerRefStatusText,
      getWorkerRefStatusColor
    };
  }
};
</script>

<style scoped>
.extra-fields-list {
  margin-bottom: 16px;
}

.empty-state {
  padding: 24px;
  text-align: center;
}

.fields-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.worker-ref-section {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.section-title {
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #595959;
}

.worker-ref-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.worker-ref-item {
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}

.worker-ref-header {
  margin-bottom: 6px;
}

.worker-ref-name {
  font-size: 13px;
  color: #262626;
}

.worker-ref-placeholder code {
  font-size: 12px;
  color: #666;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
}

.worker-ref-value {
  margin-top: 8px;
  padding: 8px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}

.worker-ref-value pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.5;
  color: #595959;
}

.field-item { 
  padding: 12px;
  /* background 由内联样式设置 */
  border-radius: 4px;
  border: 2px solid transparent;
  transition: all 0.3s;
}

.field-item-clickable {
  cursor: pointer;
}

.field-item-clickable:hover {
  border-color: #52c41a;
  box-shadow: 0 2px 8px rgba(82, 196, 26, 0.2);
  transform: translateY(-1px);
}

.field-item-clickable:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(82, 196, 26, 0.3);
}

.field-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.field-name {
  font-weight: 500;
  font-size: 14px;
  flex: 1;
}

.field-placeholder {
  margin-bottom: 8px;
}

.field-placeholder code {
  font-size: 12px;
  color: #666;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
}

.field-value {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e8e8e8;
}

.value-preview {
  font-size: 12px;
  color: #666;
  line-height: 1.6;
}

.value-preview.value-expandable {
  max-height: 200px;
  overflow-y: auto;
}

.text-preview {
  white-space: pre-wrap;
  word-break: break-all;
}

.array-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.array-item {
  padding: 4px 8px;
  background: #f0f0f0;
  border-radius: 3px;
  font-size: 12px;
  line-height: 1.5;
}

.more-text {
  padding: 4px 8px;
  color: #999;
  font-style: italic;
  font-size: 11px;
}
</style>

